import React, {
  createContext,
  FunctionComponent,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import Alert from "react-s-alert";
import {connect} from "react-redux";
import {omit} from "lodash";
import {useLocation} from "react-router-dom";
import {Admission, ChecklistItem} from "@reside/reside-api-admission";

import {AdmissionsService} from "../../services/AdmissionsService";
import {useAdmissionsByStatusQueryUtils} from "./hooks";
import {getFacilityFeatureFlags} from "../../services/FacilityService";
import {select} from "../../store";

/**
 * TODO: the facilities should be accessed from separate hook
 */
type StateProps = {
  facilityFlagsById: Record<string, ReturnType<typeof getFacilityFeatureFlags>>;
  activeFacilitiesIds: string[] | null;
};

/**
 * AdmissionId -> checklistItemId -> error message.
 */
type ChecklistErrors = Record<string, string>;
type ChecklistErrorsByAdmission = Record<string, ChecklistErrors>;

type ContextProps = {
  getAdmissionChecklistErrors: (admissionId: string) => ChecklistErrors;
  setAdmissionChecklistErrors: (
    admissionId: string,
    errors: ChecklistErrors,
  ) => void;
  clearAdmissionChecklistErrors: (
    admissionId: string,
    checklistItemId: string,
  ) => void;
  resetAdmissionChecklistErrors: () => void;
};

export const DashboardAdmissionsContext = createContext<
  StateProps & ContextProps
>({
  facilityFlagsById: {},
  activeFacilitiesIds: [],
  getAdmissionChecklistErrors: () => ({}),
  setAdmissionChecklistErrors: () => {},
  clearAdmissionChecklistErrors: () => {},
  resetAdmissionChecklistErrors: () => {},
});

export const useDashboardAdmissionsContext = () => {
  const {facilityFlagsById, activeFacilitiesIds, ...context} = useContext(
    DashboardAdmissionsContext,
  );

  const {
    refetchAllAdmissionsByStatusQueries,
    setAdmissionsByStatusQueryData,
    getAdmissionsByStatusQueryData,
  } = useAdmissionsByStatusQueryUtils();

  const refreshDashboard = () => {
    refetchAllAdmissionsByStatusQueries();
  };

  const updateAdmission = ({admission}: {admission: Admission}) => {
    // Try to find cached data with different starredOnly combinations
    const baseParams = {
      status: admission.status,
      facilityIds: activeFacilitiesIds,
    };

    let currentData = getAdmissionsByStatusQueryData(baseParams);
    let actualParams = baseParams;

    // If not found, try with starredOnly: true
    if (!currentData) {
      const starredParamsGrid = {
        ...baseParams,
        starredOnly: true,
        viewMode: "grid" as const,
      };
      currentData = getAdmissionsByStatusQueryData(starredParamsGrid);
      if (currentData) {
        actualParams = starredParamsGrid;
      } else {
        const starredParamsList = {
          ...baseParams,
          starredOnly: true,
          viewMode: "list" as const,
        };
        currentData = getAdmissionsByStatusQueryData(starredParamsList);
        if (currentData) {
          actualParams = starredParamsList;
        }
      }
    }

    // If still not found, try with starredOnly: false
    if (!currentData) {
      const unstarredParamsGrid = {
        ...baseParams,
        starredOnly: false,
        viewMode: "grid" as const,
      };
      currentData = getAdmissionsByStatusQueryData(unstarredParamsGrid);
      if (currentData) {
        actualParams = unstarredParamsGrid;
      } else {
        const unstarredParamsList = {
          ...baseParams,
          starredOnly: false,
          viewMode: "list" as const,
        };
        currentData = getAdmissionsByStatusQueryData(unstarredParamsList);
        if (currentData) {
          actualParams = unstarredParamsList;
        }
      }
    }

    // Skip update if no cached data exists
    if (!currentData) {
      return;
    }

    setAdmissionsByStatusQueryData(actualParams, {
      ...currentData,
      pages: currentData.pages.map(page => {
        return {
          ...page,
          content: page?.content?.map(apiAdmission =>
            apiAdmission.id === admission.id ? admission : apiAdmission,
          ),
        };
      }),
    });
  };

  const patchAdmissionChecklistItem = async ({
    admissionId,
    checklistItemId,
    data,
  }: {
    admissionId: string;
    checklistItemId: string;
    data: ChecklistItem;
  }) => {
    try {
      const {data: admission} =
        await AdmissionsService.patchAdmissionChecklistItem({
          admissionId,
          checklistItemId,
          data,
        });

      updateAdmission({admission: admission as any});
    } catch (error) {
      Alert.error("Failed to update dashboard checkbox. Please try reloading.");
    }
  };

  return {
    facilityFlagsById,
    updateAdmission,
    refreshDashboard,
    patchAdmissionChecklistItem,
    activeFacilitiesIds,
    ...context,
  };
};

const DashboardAdmissionsContextProviderRenderer: FunctionComponent<
  PropsWithChildren<StateProps>
> = ({children, facilityFlagsById, activeFacilitiesIds}) => {
  const {pathname} = useLocation();

  const [checklistErrors, setChecklistErrors] =
    useState<ChecklistErrorsByAdmission>({});

  const getAdmissionChecklistErrors = useCallback(
    (admissionId: string) => checklistErrors[admissionId],
    [checklistErrors],
  );

  const setAdmissionChecklistErrors = useCallback(
    (admissionId: string, errors: ChecklistErrors) => {
      setChecklistErrors({...checklistErrors, [admissionId]: errors});
    },
    [checklistErrors],
  );

  const clearAdmissionChecklistErrors = useCallback(
    (admissionId: string, checklistItemId: string) => {
      setChecklistErrors({
        ...checklistErrors,
        [admissionId]: omit(
          checklistErrors[admissionId] ?? {},
          checklistItemId,
        ),
      });
    },
    [checklistErrors],
  );

  const resetAdmissionChecklistErrors = useCallback(
    () => setChecklistErrors({}),
    [],
  );

  /**
   * Clear old errors when leaving dashboard.
   */
  useEffect(() => {
    if (pathname !== "/admin/dashboard") {
      resetAdmissionChecklistErrors();
    }
  }, [resetAdmissionChecklistErrors, pathname]);

  return (
    <DashboardAdmissionsContext.Provider
      value={{
        facilityFlagsById,
        activeFacilitiesIds,
        getAdmissionChecklistErrors,
        setAdmissionChecklistErrors,
        clearAdmissionChecklistErrors,
        resetAdmissionChecklistErrors,
      }}
    >
      {children}
    </DashboardAdmissionsContext.Provider>
  );
};

export const DashboardAdmissionsContextProvider = connect<StateProps>(() =>
  select((models: any) => ({
    activeFacilitiesIds: models.adminSession.activeFacilitiesIds,
    facilityFlagsById: models.adminSession.facilityFlagsById,
  })),
)(DashboardAdmissionsContextProviderRenderer);
